import {Injectable, Signal, WritableSignal} from '@angular/core';
import {sitesStore} from './sites.store';
import {filter, map} from 'rxjs/operators';
import {TrialStatus} from '../../../services/site-details.service';
import {combineLatest, Observable} from 'rxjs';
import {SitesQueryResponse} from '../../../types/site.type';
import {Data} from '../../../types/tunings';
import {select} from '@ngneat/elf';
import {SiteSearchEntryDto, SiteSearchResultDto} from '../../../../userway/sdk/UserWayApiClient';
import {getSiteIcon} from '../../utils/getSiteIcon';
import {toSignal} from '@angular/core/rxjs-interop';

@Injectable({providedIn: 'root'})
export class SitesQuery {
  public activeSites$: Observable<SitesQueryResponse>;
  public activePdfSites$: Observable<SitesQueryResponse>;
  public deletedSites$: Observable<SitesQueryResponse>;
  public pendingSites$: Observable<SitesQueryResponse>;
  public siteIsLoading$: Observable<boolean>;
  public currentSite$: Observable<SiteSearchEntryDto>;
  public siteTunings$: Observable<Data>;
  public siteNotFound$: Observable<boolean>;
  public siteCount$: Observable<number>;
  public trialInfo$: Observable<TrialStatus>;
  public activeSitesDataIsLoading$: Observable<boolean>;
  public deletedSitesDataIsLoading$: Observable<boolean>;
  public sitesDataIsLoading$: Observable<boolean>;

  constructor() {
    this.activeSites$ = sitesStore.pipe(
      select(state => state.activeSites),
      map(this.mapActiveSiteDataToNew),
    );
    this.activePdfSites$ = sitesStore.pipe(
      select(state => state.activePdfSites),
      map(this.mapActiveSiteDataToNew),
    );

    this.deletedSites$ = sitesStore.pipe(
      select(state => state.deletedSites),
      map(this.mapDeletedAndReviewedSiteData),
    );

    this.pendingSites$ = sitesStore.pipe(
      select(state => state.pendingSites),
      map(this.mapDeletedAndReviewedSiteData),
    );

    this.siteIsLoading$ = sitesStore.pipe(select(state => state.currentSiteIsLoading));
    this.currentSite$ = sitesStore.pipe(select(state => state.currentSite));
    this.siteTunings$ = sitesStore.pipe(select(state => state.siteTunings));
    this.siteNotFound$ = sitesStore.pipe(select(state => state.siteNotFound));
    this.siteCount$ = sitesStore.pipe(select(state => state.sitesCount));
    this.trialInfo$ = sitesStore.pipe(select(state => state.trial));
    this.sitesDataIsLoading$ = sitesStore.pipe(select(state => state.isLoading));
    this.activeSitesDataIsLoading$ = sitesStore.pipe(select(state => state.isLoading));
    this.deletedSitesDataIsLoading$ = sitesStore.pipe(select(state => state.isLoading));
    this.sitesDataIsLoading$ = combineLatest([this.activeSitesDataIsLoading$, this.deletedSitesDataIsLoading$]).pipe(
      map(([loading1, loading2]) => loading1 || loading2),
    );
  }

  mapActiveSiteDataToNew(sitesData: SiteSearchResultDto): SitesQueryResponse {
    return {
      sites: sitesData.sites,
      sitesTotal: sitesData.sitesTotal,
    };
  }

  mapDeletedAndReviewedSiteData(sitesData: SiteSearchResultDto) {
    return {
      sites: sitesData.sites.map(site => ({
        ...site,
        iconUrl: site.iconUrl || getSiteIcon(site.host),
      })),
      sitesTotal: sitesData.sitesTotal || sitesData.sites.length,
    };
  }

  getLoadedSite$(): Observable<SiteSearchEntryDto> {
    return combineLatest([this.currentSite$, this.siteIsLoading$]).pipe(
      filter(([site, isLoading]) => !isLoading && !!site),
      map(([site]) => {
        if (!site) {
          throw new Error('Site is not found or available');
        }
        return site;
      }),
    );
  }
}
